import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

export class StringImpulse extends StringModule {
  private originObservers = new WeakMap<StringObject, MutationObserver>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "impulse";

    this.attributesToMap.push(
      { key: "position-strength", type: "number", fallback: this.settings["position-strength"] },
      { key: "position-tension", type: "number", fallback: this.settings["position-tension"] },
      { key: "position-friction", type: "number", fallback: this.settings["position-friction"] },
      {
        key: "position-max-velocity",
        type: "number",
        fallback: this.settings["position-max-velocity"],
      },
      {
        key: "position-update-threshold",
        type: "number",
        fallback: this.settings["position-update-threshold"],
      },

      // rotate
      { key: "rotation-strength", type: "number", fallback: this.settings["rotation-strength"] },
      { key: "rotation-tension", type: "number", fallback: this.settings["rotation-tension"] },
      { key: "rotation-friction", type: "number", fallback: this.settings["rotation-friction"] },
      {
        key: "rotation-max-angular-velocity",
        type: "number",
        fallback: this.settings["rotation-max-angular-velocity"],
      },
      { key: "rotation-max-angle", type: "number", fallback: this.settings["rotation-max-angle"] },
      {
        key: "rotation-update-threshold",
        type: "number",
        fallback: this.settings["rotation-update-threshold"],
      },

      { key: "max-offset", type: "number", fallback: this.settings["max-offset"] },
      { key: "sleep-epsilon", type: "number", fallback: this.settings["sleep-epsilon"] },
      { key: "continuous-push", type: "boolean", fallback: this.settings["continuous-push"] },
      {
        key: "rotation-origin",
        type: "string",
        fallback: this.settings["rotation-origin"] ?? "center center",
      }
    );
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);

    object.setProperty("offset-x", 0);
    object.setProperty("offset-y", 0);
    object.setProperty("velocity-x", 0);
    object.setProperty("velocity-y", 0);

    object.setProperty("angle-deg", 0);
    object.setProperty("ang-vel-deg", 0);

    object.setProperty("__prev-css-x", 0);
    object.setProperty("__prev-css-y", 0);
    object.setProperty("__prev-css-rot", 0);

    object.setProperty("__push-latch", false);
    object.setProperty("__rotate-latch", false);

    // Cache parsed rotation origin
    this.cacheRotationOrigin(object);

    // Watch for rotation-origin attribute changes
    this.observeRotationOrigin(object);

    this.hover.track(object);
    this.centers.attach(object);
  }

  override onObjectDisconnected(object: StringObject): void {
    this.hover.untrack(object);
    this.centers.detach(object);

    // Cleanup rotation-origin observer
    const observer = this.originObservers.get(object);
    if (observer) {
      observer.disconnect();
      this.originObservers.delete(object);
    }
  }

  override onMouseMove(_e?: MouseEvent): void {
    if (!_e) return;

    const cursorVelocityX = this.data.cursor.velocityX;
    const cursorVelocityY = this.data.cursor.velocityY;
    if (cursorVelocityX === 0 && cursorVelocityY === 0) return;

    const mouseX = this.data.cursor.targetX;
    const mouseY = this.data.cursor.targetY;

    for (const object of this.objects) {
      const isUnderCursor = this.hover.isActive(object);

      if (isUnderCursor) {
        const rect = object.htmlElement.getBoundingClientRect();

        {
          const w = rect.width || 1;
          const side01 = Math.max(0, Math.min(1, (this.data.cursor.targetX - rect.left) / w));
          this.events.emit(`object:impulse:${object.id}:side`, { value: side01 });
        }

        const positionStrength = object.getProperty<number>("position-strength") || 0;
        if (positionStrength !== 0) {
          const isContinuousPush = object.getProperty<boolean>("continuous-push") ?? true;
          const isLatched = object.getProperty<boolean>("__push-latch") === true;
          if (isContinuousPush || !isLatched) {
            let objectVelocityX = object.getProperty<number>("velocity-x") || 0;
            let objectVelocityY = object.getProperty<number>("velocity-y") || 0;
            objectVelocityX += cursorVelocityX * positionStrength;
            objectVelocityY += cursorVelocityY * positionStrength;
            object.setProperty("velocity-x", objectVelocityX);
            object.setProperty("velocity-y", objectVelocityY);
            if (!isContinuousPush) object.setProperty("__push-latch", true);
          }
        }

        const rotationStrength = object.getProperty<number>("rotation-strength") ?? 0.75;
        if (rotationStrength !== 0) {
          const isContinuousPush = object.getProperty<boolean>("continuous-push") ?? true;
          const isLatched = object.getProperty<boolean>("__rotate-latch") === true;

          if (isContinuousPush || !isLatched) {
            const { centerX, centerY } = this.getRotationOriginFromRect(object, rect);
            const relativeX = mouseX - centerX;
            const relativeY = mouseY - centerY;
            const torque = relativeX * cursorVelocityY - relativeY * cursorVelocityX;

            let angularVelocityDeg = object.getProperty<number>("ang-vel-deg") || 0;
            angularVelocityDeg += torque * rotationStrength * 0.02;
            object.setProperty("ang-vel-deg", angularVelocityDeg);
            if (!isContinuousPush) object.setProperty("__rotate-latch", true);
          }
        }
      }
    }
  }

  /**
   * Parses and caches the rotation origin for an object.
   * Called once on object connection and when attribute changes.
   */
  private cacheRotationOrigin(object: StringObject): void {
    const originValue = object.getProperty<string>("rotation-origin") ?? "center center";
    const { x, y } = this.tools.originParser.toNormalized({ value: originValue });
    object.setProperty("__rotation-origin-x", x);
    object.setProperty("__rotation-origin-y", y);
  }

  /**
   * Observes changes to the rotation-origin attribute and re-caches when changed.
   */
  private observeRotationOrigin(object: StringObject): void {
    const attrName = "string-rotation-origin";
    const dataAttrName = "data-string-rotation-origin";

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === attrName || mutation.attributeName === dataAttrName)
        ) {
          // Re-read attribute and update cache
          const newValue =
            object.htmlElement.getAttribute(attrName) ??
            object.htmlElement.getAttribute(dataAttrName);
          if (newValue !== null) {
            object.setProperty("rotation-origin", newValue);
          }
          this.cacheRotationOrigin(object);
          break;
        }
      }
    });

    observer.observe(object.htmlElement, {
      attributes: true,
      attributeFilter: [attrName, dataAttrName],
    });

    this.originObservers.set(object, observer);
  }

  /**
   * Calculates the rotation origin point using cached normalized values and provided rect.
   * Avoids re-parsing origin string and extra getBoundingClientRect calls.
   */
  private getRotationOriginFromRect(
    object: StringObject,
    rect: DOMRect
  ): { centerX: number; centerY: number } {
    const normalizedX = object.getProperty<number>("__rotation-origin-x") ?? 0.5;
    const normalizedY = object.getProperty<number>("__rotation-origin-y") ?? 0.5;

    return {
      centerX: rect.left + rect.width * normalizedX,
      centerY: rect.top + rect.height * normalizedY,
    };
  }

  onFrame(_: StringData): void {
    for (const objectItem of this.objects) {
      const isHovered = this.hover.isActive(objectItem);

      if (!isHovered && objectItem.getProperty<boolean>("__push-latch") === true) {
        objectItem.setProperty("__push-latch", false);
      }
      if (!isHovered && objectItem.getProperty<boolean>("__rotate-latch") === true) {
        objectItem.setProperty("__rotate-latch", false);
      }
    }

    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];

      let offsetX = object.getProperty<number>("offset-x") || 0;
      let offsetY = object.getProperty<number>("offset-y") || 0;
      let velocityX = object.getProperty<number>("velocity-x") || 0;
      let velocityY = object.getProperty<number>("velocity-y") || 0;

      const positionTension = object.getProperty<number>("position-tension") ?? 0.05;
      const positionFriction = object.getProperty<number>("position-friction") ?? 0.15;
      const maxVelocity = object.getProperty<number>("position-max-velocity") ?? 120;
      const maxOffset = object.getProperty<number>("max-offset") ?? 220;

      velocityX -= positionTension * offsetX;
      velocityY -= positionTension * offsetY;

      const velocityDecay = 1 - positionFriction;
      velocityX *= velocityDecay;
      velocityY *= velocityDecay;

      if (velocityX > maxVelocity) velocityX = maxVelocity;
      else if (velocityX < -maxVelocity) velocityX = -maxVelocity;
      if (velocityY > maxVelocity) velocityY = maxVelocity;
      else if (velocityY < -maxVelocity) velocityY = -maxVelocity;

      offsetX += velocityX;
      offsetY += velocityY;

      if (offsetX > maxOffset) offsetX = maxOffset;
      else if (offsetX < -maxOffset) offsetX = -maxOffset;
      if (offsetY > maxOffset) offsetY = maxOffset;
      else if (offsetY < -maxOffset) offsetY = -maxOffset;

      let angleDeg = object.getProperty<number>("angle-deg") || 0;
      let angularVelocityDeg = object.getProperty<number>("ang-vel-deg") || 0;

      const rotationTension = object.getProperty<number>("rotation-tension") ?? 0.06;
      const rotationFriction = object.getProperty<number>("rotation-friction") ?? 0.18;
      const maxAngularVelocityDeg =
        object.getProperty<number>("rotation-max-angular-velocity") ?? 6;
      const maxRotationAngleDeg = object.getProperty<number>("rotation-max-angle") ?? 18;

      angularVelocityDeg -= rotationTension * angleDeg;
      angularVelocityDeg *= 1 - rotationFriction;

      if (angularVelocityDeg > maxAngularVelocityDeg) angularVelocityDeg = maxAngularVelocityDeg;
      else if (angularVelocityDeg < -maxAngularVelocityDeg)
        angularVelocityDeg = -maxAngularVelocityDeg;

      angleDeg += angularVelocityDeg;

      if (angleDeg > maxRotationAngleDeg) {
        angleDeg = maxRotationAngleDeg;
        angularVelocityDeg *= 0.35;
      } else if (angleDeg < -maxRotationAngleDeg) {
        angleDeg = -maxRotationAngleDeg;
        angularVelocityDeg *= 0.35;
      }

      const sleepEpsilon = object.getProperty<number>("sleep-epsilon") ?? 0.01;
      const isPositionAsleep =
        velocityX * velocityX + velocityY * velocityY < sleepEpsilon * sleepEpsilon &&
        offsetX * offsetX + offsetY * offsetY < sleepEpsilon * sleepEpsilon;

      const isRotationAsleep =
        Math.abs(angularVelocityDeg) < sleepEpsilon && Math.abs(angleDeg) < sleepEpsilon;

      if (isPositionAsleep) {
        if (velocityX || velocityY || offsetX || offsetY) {
          object.setProperty("offset-x", 0);
          object.setProperty("offset-y", 0);
          object.setProperty("velocity-x", 0);
          object.setProperty("velocity-y", 0);
          offsetX = offsetY = velocityX = velocityY = 0;
        }
      } else {
        object.setProperty("offset-x", offsetX);
        object.setProperty("offset-y", offsetY);
        object.setProperty("velocity-x", velocityX);
        object.setProperty("velocity-y", velocityY);
      }

      if (isRotationAsleep) {
        if (angleDeg || angularVelocityDeg) {
          object.setProperty("angle-deg", 0);
          object.setProperty("ang-vel-deg", 0);
          angleDeg = angularVelocityDeg = 0;
        }
      } else {
        object.setProperty("angle-deg", angleDeg);
        object.setProperty("ang-vel-deg", angularVelocityDeg);
      }

      const positionUpdateThreshold =
        object.getProperty<number>("position-update-threshold") ?? 0.1;
      const rotationUpdateThreshold =
        object.getProperty<number>("rotation-update-threshold") ?? 0.15;

      const prevCssX = object.getProperty<number>("__prev-css-x") || 0;
      const prevCssY = object.getProperty<number>("__prev-css-y") || 0;
      const prevCssRot = object.getProperty<number>("__prev-css-rot") || 0;

      const cssTranslateX = Math.round(offsetX * 10) / 10;
      const cssTranslateY = Math.round(offsetY * 10) / 10;
      const cssRotateDeg = Math.round(angleDeg * 10) / 10;

      const needsPositionUpdate =
        Math.abs(cssTranslateX - prevCssX) > positionUpdateThreshold ||
        Math.abs(cssTranslateY - prevCssY) > positionUpdateThreshold;

      const needsRotationUpdate = Math.abs(cssRotateDeg - prevCssRot) > rotationUpdateThreshold;

      if (needsPositionUpdate || needsRotationUpdate) {
        this.applyToElementAndConnects(object, (el) => {
          if (needsPositionUpdate) {
            el.style.setProperty("--push-x", String(cssTranslateX));
            el.style.setProperty("--push-y", String(cssTranslateY));
            this.events.emit(`object:impulse:${object.id}:move`, {
              x: cssTranslateX,
              y: cssTranslateY,
            });
          }
          if (needsRotationUpdate) {
            el.style.setProperty("--push-rotation", String(cssRotateDeg));
            this.events.emit(`object:impulse:${object.id}:rotate`, { rotation: cssRotateDeg });
          }
        });

        if (needsPositionUpdate) {
          object.setProperty("__prev-css-x", cssTranslateX);
          object.setProperty("__prev-css-y", cssTranslateY);
        }
        if (needsRotationUpdate) {
          object.setProperty("__prev-css-rot", cssRotateDeg);
        }
      }
    }
  }
}
